'use client'

import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { type RefObject, useLayoutEffect, useMemo } from 'react'

import { isFullChatPath } from './excluded-paths'

/**
 * Logical workspace surfaces the orchestrator understands. Mirrors the
 * enum in
 * [`app/(app)/(workspace)/use-workspace-active-view.ts`], but the
 * classifier here returns `null` for non-workspace routes instead of
 * defaulting to `'emails-canvas'`.
 *
 * The workspace shell needs a definite value (it drives CSS visibility
 * on the three always-mounted canvas surfaces, so an unknown path must
 * resolve to a visible pane). The GLOBAL chat — which now lives on every
 * authenticated page — must NOT claim the user is on the emails canvas
 * when they are actually on `/brand` or `/audience`; it reports `null`
 * (→ `activeView: undefined`) off-workspace and relies on `currentPath`
 * for location context. The two contracts differ on purpose, so the
 * tiny pathname matcher is duplicated rather than coupling a layout-
 * level component to the workspace route group.
 */
export type WorkspaceActiveView =
  | 'emails-canvas'
  | 'emails-list'
  | 'automations-canvas'
  | 'automations-list'

/**
 * Pure pathname → workspace view classifier. Returns `null` for any
 * route that is not part of the `/emails*` / `/automations*` workspace.
 *
 * Order matters: the more-specific `/list` checks come BEFORE the
 * broader prefix checks.
 */
export function pathnameToWorkspaceViewOrNull(
  pathname: string | null | undefined
): WorkspaceActiveView | null {
  if (!pathname) {
    return null
  }
  // No `/automations/list` route post-decouple — /automations (overview +
  // table) and /automations/{id} (single-automation canvas) both map to
  // the automations surface.
  if (pathname.startsWith('/automations')) {
    return 'automations-canvas'
  }
  if (pathname.startsWith('/emails/list')) {
    return 'emails-list'
  }
  // Exact `/emails` is the group-cards grid — not a canvas the
  // orchestrator should treat as emails-canvas. Group canvases
  // (`/emails/{grp_*|ungrouped}`) stay emails-canvas.
  if (pathname === '/emails' || pathname === '/emails/') {
    return null
  }
  if (pathname.startsWith('/emails')) {
    return 'emails-canvas'
  }
  return null
}

/**
 * The salient ROUTE params for the page the user is viewing — the bits the
 * orchestrator can act on directly. Today that is the active automation /
 * email on their per-id routes (`/automations/{automationId}`,
 * `/emails/{emailId}`). Bounded + opaque-id-only by construction (route
 * params, not arbitrary query strings) so nothing sensitive leaks into the
 * prompt; the full querystring already rides along in `currentPath`.
 */
export type CurrentUrlParams = {
  automationId?: string
  emailId?: string
  /** `/emails/{grp_*|ungrouped}` route segment, as-is. */
  groupId?: string
}

export type ActiveSurface = {
  /** Current pathname (no search params). */
  pathname: string
  /** Workspace view, or `undefined` when the user is off-workspace. */
  activeView: WorkspaceActiveView | undefined
  /**
   * Full path the user is viewing (`pathname` + querystring) — the query
   * params ARE the page's state, so the orchestrator gets them too.
   * `undefined` on the dedicated full-page chat (`/chat/<id>`): there the
   * user is "just looking at the chat", so there is no page to report.
   */
  currentPath: string | undefined
  /**
   * Salient route params (e.g. the active `automationId`). `undefined`
   * when there are none, or on the full-page chat (mirrors `currentPath`).
   * The route server-enriches `automationId` into the orchestrator's
   * active-automation context — this is what makes the agent aware of the
   * automation the user is viewing without a manual "Add to chat".
   */
  currentUrlParams: CurrentUrlParams | undefined
}

function firstParamString(
  value: string | Array<string> | undefined
): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined
}

/**
 * Pure resolver for the structured route params the orchestrator receives.
 * Returns `undefined` on the full-page chat (`/chat/<id>`) — same contract
 * as `resolveCurrentPath` — and whenever no salient param is present.
 * Exported (separate from the hook) so it can be unit-tested without a
 * Next.js navigation context.
 */
export function resolveCurrentUrlParams(
  pathname: string | null | undefined,
  params: Record<string, string | Array<string> | undefined> | null | undefined
): CurrentUrlParams | undefined {
  if (isFullChatPath(pathname)) {
    return
  }
  const automationId = firstParamString(params?.automationId)
  const emailId = firstParamString(params?.emailId)
  const groupId = firstParamString(params?.groupId)
  const out: CurrentUrlParams = {}
  if (automationId) {
    out.automationId = automationId
  }
  if (emailId) {
    out.emailId = emailId
  }
  if (groupId) {
    out.groupId = groupId
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/**
 * Pure resolver for the `currentPath` the orchestrator receives. Joins
 * pathname + querystring (the query params ARE the page's state), and
 * returns `undefined` on the dedicated full-page chat (`/chat/<id>`) so
 * the agent is told "the user is just looking at the chat". Exported
 * (separate from the hook) so it can be unit-tested without a Next.js
 * navigation context.
 */
export function resolveCurrentPath(
  pathname: string | null | undefined,
  search: string | null | undefined
): string | undefined {
  if (isFullChatPath(pathname)) {
    return
  }
  const resolvedPathname = pathname && pathname.length > 0 ? pathname : '/'
  const normalizedSearch = search ?? ''
  return normalizedSearch
    ? `${resolvedPathname}?${normalizedSearch}`
    : resolvedPathname
}

/**
 * Reports the chat's view of "where is the user right now" for the
 * orchestrator request body. Used by the global chat thread. Pure
 * Next.js navigation hooks (no effects).
 */
export function useActiveSurface(): ActiveSurface {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const params = useParams()
  const search = searchParams?.toString() ?? ''
  // `useParams()` returns a stable-enough object; key the memo on the
  // resolved param values so identity churn doesn't rebuild every render.
  const automationId = firstParamString(params?.automationId)
  const emailId = firstParamString(params?.emailId)
  const groupId = firstParamString(params?.groupId)
  return useMemo(
    () => ({
      pathname: pathname ?? '/',
      activeView: pathnameToWorkspaceViewOrNull(pathname) ?? undefined,
      currentPath: resolveCurrentPath(pathname, search),
      currentUrlParams: resolveCurrentUrlParams(pathname, {
        automationId,
        emailId,
        groupId,
      }),
    }),
    [pathname, search, automationId, emailId, groupId]
  )
}

/**
 * Leaf that mirrors {@link useActiveSurface} into `surfaceRef` for readers
 * that only need the surface at call time (the chat thread's request body,
 * built on submit). The URL subscription re-renders this null-rendering
 * leaf instead of its host, so query-param writes (canvas selection,
 * `?chatView=`) stop re-rendering the chat thread and its submit callbacks
 * keep their identity across navigations. The ref is written in a layout
 * effect, before any passive effect (such as the launcher hand-off's mount
 * submit) can read it.
 */
export function ActiveSurfaceTracker({
  surfaceRef,
}: {
  surfaceRef: RefObject<ActiveSurface | null>
}): null {
  const surface = useActiveSurface()
  useLayoutEffect(() => {
    surfaceRef.current = surface
  }, [surface, surfaceRef])
  return null
}
